import styled from 'styled-components';

import { bidiText } from 'components/bidi';

import { ctrlBase, fieldContainer, fieldLabel, fieldMessage } from 'components/form/base';

/**
 * The notification screen's own multi-select.
 *
 * It is a near-copy of `components/form/MultiDropdown` that exists for one
 * reason the shared control does not cover: it searches the **server** as you
 * type (`onChangeValue` + `loading`), because the user list is thousands long
 * and is not held in the page. That is why it was not simply replaced here.
 *
 * Everything else about it now comes from the same primitives as the shared
 * one, so the two look identical. What went: a 48px input on a 12px radius,
 * `#eb2b2b` for the error border, `#4d4d4d` option text at 14px, a `#cfeeec`
 * pill for a selected person, and a 20px teal circle-plus per row.
 */

export const inputContainer = styled.div`
  ${fieldContainer}
`;

export const input = styled('input')`
  ${ctrlBase}
  padding-right: var(--s6);
  cursor: pointer;
`;

export const label = styled('label')<{ labelColor?: string | undefined }>`
  ${fieldLabel}
`;

export const message = styled.span`
  ${fieldMessage}
`;

export const icon = styled.div`
  position: absolute;
  right: var(--s3);
  bottom: 0;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--text-muted);
  line-height: 0;
`;

export const iconSelect = styled.div`
  position: absolute;
  left: var(--s3);
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

/* The floating panel — the shared control's, including its layer and cap. */
export const dropDownItems = styled.ul`
  position: absolute;
  top: calc(100% + var(--s1));
  left: 0;
  width: 100%;
  z-index: var(--z-overlay);
  display: flex;
  flex-direction: column;
  gap: var(--s1);
  padding: var(--s2);
  margin: 0;
  list-style: none;
  background: var(--surface);
  border: 1px solid transparent;
  border-radius: var(--r-md);
  max-height: 0;
  overflow: hidden;
  visibility: hidden;
  transition: max-height 0.2s;

  &.show {
    max-height: var(--h-overlay-max);
    overflow-y: auto;
    overscroll-behavior: contain;
    visibility: visible;
    border-color: var(--line);
    box-shadow: var(--sh-2);
  }

  &::-webkit-scrollbar {
    width: var(--s2);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--line-strong);
    border-radius: var(--r-pill);
    border: 2px solid var(--surface);
  }
`;

/* One option — the design's `.check` row. Names are payload, so they get bidi. */
export const dropDownItem = styled.li`
  ${bidiText};
  display: flex;
  align-items: center;
  gap: var(--s2);
  flex: 0 0 auto;
  padding: var(--s2) var(--s3);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  background: var(--surface);
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s;

  &:hover {
    border-color: var(--line-strong);
    background: var(--surface-alt);
  }
`;

export const text = styled.span`
  ${bidiText};
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
`;

export const img = styled.img``;

export const arrowImg = styled.img``;

export const CheckText = styled.span`
  ${bidiText};
  flex: 1;
  min-width: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const InputBox = styled.div`
  position: relative;
`;

/* ── selected values — the design's `.tokens` / `.token` ───────────────── */

export const ActiveItems = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: var(--s1);
  margin-top: var(--s2);
  padding: 0;
  list-style: none;
`;

export const ActiveItem = styled.li`
  ${bidiText};
  display: inline-flex;
  align-items: center;
  gap: var(--s1);
  padding: var(--s1) var(--s1) var(--s1) var(--s2);
  border-radius: var(--r-sm);
  background: var(--primary-soft);
  color: var(--primary);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
`;

export const ActiveItemButton = styled.button`
  display: grid;
  place-items: center;
  width: 15px;
  height: 15px;
  padding: 0;
  border: none;
  border-radius: var(--r-sm);
  background: none;
  color: inherit;
  cursor: pointer;
  line-height: 0;

  &:hover {
    background: var(--surface);
  }

  svg {
    width: 9px;
    height: 9px;
  }
`;

/* The add affordance on an option row, as a quiet icon rather than a solid
   teal disc — the row itself already reads as the target. */
export const AddButton = styled.button`
  display: grid;
  place-items: center;
  width: var(--h-btn-sm);
  height: var(--h-btn-sm);
  flex: 0 0 var(--h-btn-sm);
  padding: 0;
  border: none;
  border-radius: var(--r-sm);
  background: none;
  color: var(--text-faint);
  cursor: pointer;
  line-height: 0;
  transition:
    background-color 0.12s,
    color 0.12s;

  &:hover:not(:disabled) {
    background: var(--primary-soft);
    color: var(--primary);
  }

  &:disabled {
    color: var(--primary);
    cursor: not-allowed;
  }

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.9;
  }
`;

/* Both nothings the panel can hold: searching, and nobody found. */
export const Loader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--s7);
  padding: var(--s4);
  font-size: var(--fs-body);
  color: var(--text-faint);
`;
